import { useCallback } from 'react';
import { ScrollView } from 'react-native';
import { Flex, RemoteImage, Typography } from '@banhala/ui-kit';
import type {
  HorizontalImageChipListProps,
  HorizontalImageChipEntity,
} from './types';
import { bridge } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';
import type { IScreensLogging } from '../../../../api/types/IScreenLogging';

export const HorizontalImageChipList = ({
  entity,
  body,
  logging,
}: HorizontalImageChipListProps) => {
  if (!entity || entity.length === 0) return null;

  const chipSize = body?.item.chip_size ?? 'LARGE';
  const chipHeight = chipSize === 'LARGE' ? 40 : 36;
  const imageSize = chipSize === 'LARGE' ? 32 : 28;

  return (
    <Flex>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: 8,
          gap: 4,
        }}
      >
        {entity.map((chip, index) => (
          <ImageChip
            key={chip.item.deeplink + index}
            entity={chip}
            logging={logging}
            chipHeight={chipHeight}
            imageSize={imageSize}
          />
        ))}
      </ScrollView>
    </Flex>
  );
};

const ImageChip = ({
  entity,
  logging,
  chipHeight,
  imageSize,
}: {
  entity: HorizontalImageChipEntity;
  logging: IScreensLogging;
  chipHeight: number;
  imageSize: number;
}) => {
  const { item } = entity;
  const { openDeeplink } = useOpenDeeplink();

  const handlePress = useCallback(() => {
    bridge.logAnalyticsEvent({
      name: 'CLICK_HORIZONTAL_IMAGE_CHIP',
      analytics: { ...logging?.analytics, ...entity.logging?.analytics },
      ably_only_log: { ...logging?.ably_only_log, ...entity.logging?.ably_only_log },
    });
    openDeeplink(item.deeplink);
  }, [item.deeplink, logging, entity.logging, openDeeplink]);

  return (
    <Flex.Row
      alignItems="center"
      height={chipHeight}
      borderRadius={chipHeight / 2}
      borderWidth={1}
      borderColor="border_tertiary"
      padding={{ left: 4, right: 12 }}
      gap={4}
      onPress={handlePress}
    >
      <Flex
        width={imageSize}
        height={imageSize}
        borderRadius={imageSize / 2}
        overflow="hidden"
      >
        <RemoteImage
          uri={item.representative_image}
          width={imageSize}
          height={imageSize}
          resizeMode="cover"
        />
      </Flex>
      <Typography type="subtitle3" color="content_primary" numberOfLines={1}>
        {item.name}
      </Typography>
      {item.badge != null && (
        <Flex position={'absolute'} top={-5} right={0}>
          <RemoteImage
            uri={item.badge.image}
            width={28}
            height={14}
            resizeMode="contain"
          />
        </Flex>
      )}
    </Flex.Row>
  );
};
